import React, { Fragment, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import UntaggedCostComponentsAggregates from "./UntaggedCostComponentsAggregates.jsx";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "../../components/form-components/Dropdown.jsx";
import tailwindConfig from "../../tailwind.config.js";
import resolveConfig from "tailwindcss/resolveConfig";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useKeycloak } from "@react-keycloak/web";

export const colors = resolveConfig(tailwindConfig).theme.colors;
export const API_URL = process.env.REACT_APP_API_URL;

const UntaggedCostComponents = () => {
	const [aggregatedCosts, setAggregatedCosts] = useState({});
	const [costAggregates, setCostAggregates] = useState({});
	const [filters, ] = useState([]);
	const [tagKeys, setTagKeys] = useState([]);
	const { keycloak } = useKeycloak();
	const [selectedTagKey, setSelectedTagKey] = useState({label: "All tags", value: "All tags"});

	const { startDate,  endDate } = useOutletContext();

	// initialization for the variables for the fuzzy search, the filters and the grouping
	const filtersRef = useRef();
	filtersRef.current = filters;

	function buildRequest() {
		let reqFilters = [];
		
		if (selectedTagKey.value !== "All tags") {
			// the format of the filter is different when the field is tag key
			// the operator is either is null or is not null
			// the values are the tag key that should be used to filter on (empty of filled)
			reqFilters.push({
				field: "tag key",
				values: [selectedTagKey.value],
				operator: "is null",
				is_subgroup: true,
			});
		} else {				
			reqFilters.push({
				field: "tags",
				operator: "=",
				values: [""],
				is_subgroup: true,
			});
		}

		reqFilters.push({
			field: "cost_type_grouping",
			operator: "=",
			values: ["Direct cost"],
		});

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		return {
			filters: reqFilters,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
		};
	}

	const retrieveCostKPIs = async(token, filtersRef, startDate, endDate, selectedTagKey) => {
		if (token === "" || filtersRef === undefined || startDate === null || endDate === null || selectedTagKey === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		let method = "POST";

		const requestBody = buildRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-cloud-cost-kpis", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}

	useQuery(
		["costKPIs", filtersRef, startDate, endDate, selectedTagKey],
		() => retrieveCostKPIs(keycloak.token, filtersRef, startDate, endDate, selectedTagKey),
		{
			onSuccess: (data) => {		
				setCostAggregates({
					cost: data.CloudCost.cost,
					trend_cost: JSON.parse(data.CloudCost.trend_cost),
					total_cost: data.CloudCost.total_cost,
					records: data.CloudCost.records,
					trend_records: JSON.parse(data.CloudCost.trend_records),
					total_records: data.CloudCost.total_records,
				});
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null &&
				selectedTagKey !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	function buildAggregatedRequest() {
		let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];
		let measureFormule = "";

		filtersRef.current.forEach((filter) => {
			reqFilters.push({
				field: filter.category,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		reqFilters.push({
			field: "cost_type_grouping",
			operator: "=",
			values: ["Direct cost"],
		});

		if (selectedTagKey.value !== "All tags") {
			// measureFormule = `(select elem ->> 'Key' from jsonb_array_elements(tags_jsonb) as elem where elem ->> 'Key' ilike '${selectedTagKey.value}') is null`;
			measureFormule = `(string(parse_json(regexp_extract(tags, r'({"key":"${selectedTagKey.value}"[^}]*})')).value)) is null`;
		} else {
			measureFormule = `tags = "[{\\"key\\":null,\\"value\\":null}]"`;
		}

		reqMeasures.push({name: "untagged_cost", formula: `sum(case when ${measureFormule} then total_cost else 0 end)`});
		reqMeasures.push({name: "untagged_transactions", formula: `sum(case when ${measureFormule} then 1 else 0 end)`});
		reqMeasures.push({name: "total_transactions", formula: "sum(1)"});
		reqMeasures.push({name: "untagged_cost_components", formula: `count(distinct case when ${measureFormule} then cc.cost_component_id else null end)`});
		reqMeasures.push({name: "total_cost_components", formula: "count(distinct cc.cost_component_id)"});
		reqMeasures.push({name: "total_cost", formula: "sum(total_cost)"});
		reqMeasures.push({name: "untagged_cost_per_cost_resource_type", formula: `sum(coalesce(sum(case when ${measureFormule} then total_cost else 0 end), 0)) over (partition by coalesce(crt.cost_resource_type_description, ''))`});

		reqGrouping.push("cost_resource_cluster");
		reqGrouping.push("cost_resource_type");
		reqGrouping.push("cost_component");

		return {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: false,
			sort_columns: "untagged_cost_per_cost_resource_type desc, untagged_cost desc",
		};
	}

	const retrieveTagKeys = async(token) => {
		if (token === "" ) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		let method = "GET";

		let requestOptions = {
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-tag-keys", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}

	useQuery(
		["tagKeys"],
		() => retrieveTagKeys(keycloak.token),
		{
			onSuccess: (data) => {
				var tmpData = [];
				tmpData.push({
					label: "All tags",
					value: "All tags"
				});
				
                if (data.TagKeys !== undefined) {
                    data.TagKeys.forEach((element) => {
                        var tmpElement = {
                            label: element.tag_key,    
                            value: element.tag_key
                        };
                        tmpData.push(tmpElement);
                    });
                }
				// Sort tmpData by label, keeping "All tags" first
				tmpData.sort((a, b) => {
					if (a.value === "All tags") return -1;
					if (b.value === "All tags") return 1;
					return a.label.localeCompare(b.label);
				});

				setTagKeys(tmpData);
			},
			enabled: keycloak.token !== "",
			staleTime: 5000
		}
	);

	const retrieveAggregatedCosts = async(token, filtersRef, startDate, endDate, selectedTagKey) => {
		if (token === "" || filtersRef === undefined || startDate === null || endDate === null || selectedTagKey === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)

		let method = "POST";

		const requestBody = buildAggregatedRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-cloud-cost-aggregated", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["aggregatedCosts", filtersRef, startDate, endDate, selectedTagKey],
		() => retrieveAggregatedCosts(keycloak.token, filtersRef, startDate, endDate, selectedTagKey),
		{
			onSuccess: (data) => {
				// group by cost component cluster
				const groupedDataByGroup = groupAggregatedData(data.CloudCost.details, "cost_resource_cluster");

				// group again by cost component group
				groupedDataByGroup.forEach(group => {
					group.subRows = groupAggregatedData(group.subRows, "cost_resource_type");
				});

				// Sort so that "Other" appears last
				groupedDataByGroup.sort((a, b) => {
					if (a.cost_component_cluster === "Other") return 1;
					if (b.cost_component_cluster === "Other") return -1;
					return 0;
				});

				// Also sort "Other" to last within each group's subRows
				groupedDataByGroup.forEach(group => {
					group.subRows.sort((a, b) => {
						if (a.cost_component_group === "Other") return 1;
						if (b.cost_component_group === "Other") return -1;
						return 0;
					});
				});

				setAggregatedCosts(groupedDataByGroup);
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	function groupAggregatedData(data, grouping) {		
		let preparedData = [];
		let lastGrouping = "";
		let lastGroup = {};

		if (!data ||data.length === 0) return preparedData;

		// Sort data by grouping field
		data.sort((a, b) => {
			if (a[grouping] < b[grouping]) return -1;
			if (a[grouping] > b[grouping]) return 1;
			return 0;
		});

		data.forEach((row) => {
			if (row[grouping] !== lastGrouping) {
				lastGrouping = row[grouping];
				lastGroup = {
					[grouping]: row[grouping],
					untagged_transactions: 0,
					total_transactions: 0,
					untagged_cost_components: 0,
					total_cost_components: 0,
					untagged_cost: 0,
					total_cost: 0,
					subRows: [],
				};
				preparedData.push(lastGroup);
			}

			lastGroup.untagged_transactions += row.untagged_transactions;
			lastGroup.total_transactions += row.total_transactions;
			lastGroup.untagged_cost_components += row.untagged_cost_components;
			lastGroup.total_cost_components += row.total_cost_components;
			lastGroup.untagged_cost += row.untagged_cost;
			lastGroup.total_cost += row.total_cost;
			lastGroup.subRows.push(row);
		});

		return preparedData;
	}

	return (
		<Fragment>
            <AdministrationHeader
				title="Tagging Policy"
                subTitle="Define and manage the tags that are used in the different cloud environments"
				className="text-text-3"
				icon={icon({name: "tags", style: "light"})}
			/>
			<div className="pl-6 text-left flex text-text-2 mt-6">
				<div className="ml-4 pt-1 mb-4">
					<div>
						<div className="">
							<Dropdown
                                valuePrefix="Tag category : "
								options={tagKeys}
								dropdownWidth={400}
								selectedValue={selectedTagKey}
								setSelectedValue={setSelectedTagKey}
                                backgroundColor={colors.pink6}
                                borderColor={colors.pink3}
                                textColor={colors.white}
							/>
						</div>
					</div>
				</div>

				{/* <FilterBar
					filters={filters}
					setFilters={setFilters}
				/> */}
			</div>
			<div className="text-left flex flex-auto flex-col ml-10 border-solid border-border">
				<div className="flex flex-row gap-4 flex-wrap">
					<div className="flex-shrink-0">
						<KPI
							title="Costs tagged"
							currentValue={((costAggregates.total_cost - costAggregates.cost) / costAggregates.total_cost)*100}
							// trendData={costAggregates.trend_cost}
							format="percentage"
							showChange={false}
						/>
					</div>

					<div className="flex-shrink-0">
						<KPI
							title="Transactions tagged"
							currentValue={((costAggregates.total_records - costAggregates.records) / costAggregates.total_records)*100}
							// trendData={costAggregates.trend_records}
							format="percentage"
							showChange={false}
						/>
					</div>
					<div className="flex-shrink-0">
						<KPI
							title="Costs not tagged"
							currentValue={costAggregates.cost}
							trendData={costAggregates.trend_cost}
							format="currency"
							showChange={false}
						/>
					</div>

					<div className="flex-shrink-0">
						<KPI
							title="Transactions not tagged"
							currentValue={costAggregates.records}
							trendData={costAggregates.trend_records}
							format="decimal"
							showChange={false}
						/>
					</div>
				</div>

				<div className="bg-card border border-card-bd pt-4 pb-2 my-2 rounded-card h-screen flex flex-auto flex-col">
					<UntaggedCostComponentsAggregates
						data={aggregatedCosts}
						expandAll={true}
						totalCost={costAggregates.cost}
					/>
				</div>
			</div>
		</Fragment>
	);
};

export default UntaggedCostComponents;
